/**
 * RevenueCat SDK wrapper for Mellow (Capacitor iOS/Android)
 * Handles consumable credit pack purchases on native platforms.
 * Only loaded on native — web uses Stripe.
 */

import { platform } from './native';
import { logger } from './logger';

// Credit pack product IDs → credits mapping (consumable, one-time)
const CREDIT_PRODUCT_MAP = {
  mellow_credits_1000: { credits: 1000 },
  mellow_credits_5000: { credits: 5000 },
};

// Reverse mapping: credits amount → product ID
const CREDITS_PRODUCT_MAP = {
  1000: 'mellow_credits_1000',
  5000: 'mellow_credits_5000',
};

let Purchases = null;

// Cached map of productId → localizedPriceString from StoreKit
// e.g., { 'mellow_credits_1000': '$0.99', 'mellow_credits_5000': '$4.99' }
// Populated by prefetchOfferings() after initRevenueCat.
let cachedNativePrices = {};

/**
 * Lazily import the RevenueCat SDK (only on native).
 */
async function ensurePurchases() {
  if (Purchases) return;
  const mod = await import('@revenuecat/purchases-capacitor');
  Purchases = mod.Purchases;
}

/**
 * Pre-fetch RC offerings and cache localizedPriceString for each credit product.
 * Called fire-and-forget after initRevenueCat.
 */
export async function prefetchOfferings() {
  if (!platform.isNative) return;
  try {
    const offerings = await getOfferings();
    // Use mellow_default offering by key (not necessarily the project current offering)
    const mellowOffering = offerings?.all?.['mellow_default'] ?? offerings?.current;
    if (!mellowOffering?.availablePackages) return;
    const prices = {};
    for (const pkg of mellowOffering.availablePackages) {
      const productId = pkg.product?.identifier;
      const priceStr = pkg.product?.localizedPriceString ?? pkg.product?.priceString ?? null;
      if (!productId || !priceStr) continue;
      if (CREDIT_PRODUCT_MAP[productId]) {
        prices[productId] = priceStr;
      }
    }
    cachedNativePrices = prices;
    logger.info('RC native prices cached', { keys: Object.keys(prices) });
  } catch (error) {
    logger.error('Failed to prefetch RC offerings for price cache', error);
    // Non-fatal — UI falls back to credit count only
  }
}

/**
 * Returns the cached native price map (sync, safe to call any time).
 * Keys: 'mellow_credits_1000', 'mellow_credits_5000'
 * Values: localizedPriceString from StoreKit (e.g., '$0.99')
 */
export function getNativePrices() {
  return cachedNativePrices;
}

/**
 * Initialize RevenueCat SDK. Must be called after user authentication.
 * Sets the app_user_id to our Supabase user ID so webhooks can identify the user.
 */
export async function initRevenueCat(userId) {
  if (!platform.isNative) return;

  try {
    await ensurePurchases();
    const apiKey = platform.isIOS
      ? import.meta.env.VITE_REVENUECAT_API_KEY_APPLE
      : import.meta.env.VITE_REVENUECAT_API_KEY_GOOGLE;

    if (!apiKey) {
      logger.error('RevenueCat API key not configured for platform:', platform.name);
      return;
    }

    await Purchases.configure({ apiKey, appUserID: userId });
    logger.info('RevenueCat initialized', { userId, platform: platform.name });
  } catch (error) {
    logger.error('Failed to initialize RevenueCat', error);
  }
}

/**
 * Get current RC offerings (products + prices from stores).
 */
async function getOfferings() {
  if (!platform.isNative) return null;

  try {
    await ensurePurchases();
    const result = await Purchases.getOfferings();
    return result.offerings || result;
  } catch (error) {
    logger.error('Failed to get RevenueCat offerings', error);
    return null;
  }
}

/**
 * Find a credit pack package from offerings by credit amount.
 * Returns the RC package object needed for purchasePackage().
 */
export async function findCreditPackage(credits) {
  const offerings = await getOfferings();
  if (!offerings) return null;

  const mellowOffering = offerings.all?.['mellow_default'] ?? offerings.current;
  if (!mellowOffering) return null;

  const productId = CREDITS_PRODUCT_MAP[credits];
  if (!productId) return null;

  const allPackages = mellowOffering.availablePackages || [];
  return allPackages.find(pkg => pkg.product?.identifier === productId) || null;
}

/**
 * Purchase a package (triggers native StoreKit payment sheet).
 * Returns { success, customerInfo } or { success: false, cancelled, error }.
 */
export async function purchasePackage(pkg) {
  if (!platform.isNative) return { success: false, error: 'Not on native platform' };

  try {
    await ensurePurchases();
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
    logger.info('RevenueCat purchase completed', { productId: pkg.product?.identifier });
    return { success: true, customerInfo };
  } catch (error) {
    if (error.code === 'PURCHASE_CANCELLED_ERROR' || error.userCancelled) {
      logger.info('RevenueCat purchase cancelled by user');
      return { success: false, cancelled: true };
    }
    logger.error('RevenueCat purchase failed', error);
    return { success: false, error: error.message || 'Purchase failed' };
  }
}

/**
 * Restore previous purchases (required by App Store guidelines).
 * For consumable credit packs, hasActivePurchases will be false (consumables
 * don't create entitlements), but the call is still required by §3.1.1.
 * Returns { customerInfo, hasActivePurchases } on success.
 * Throws on error so callers can show appropriate error UI.
 */
export async function restorePurchases() {
  if (!platform.isNative) return null;

  await ensurePurchases();
  const { customerInfo } = await Purchases.restorePurchases();
  logger.info('RevenueCat purchases restored');
  const hasActivePurchases = Object.keys(customerInfo?.entitlements?.active ?? {}).length > 0;
  return { customerInfo, hasActivePurchases };
}
